import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiSend, FiImage, FiX, FiAlertCircle, FiCheckCircle, FiLifeBuoy } from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function HelpdeskCreatePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ฟิลด์ฟอร์ม
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('bug'); // bug, feature, hardware, access, general
  const [priority, setPriority] = useState('medium'); // low, medium, high, critical
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ข้อมูลผู้แจ้ง (ดึงมาจาก Auth)
  const reporterName = user?.name || user?.email?.split('@')[0] || 'แอดมินทั่วไป';
  const reporterEmail = user?.email || 'admin@thundermaxhome.com';

  // จัดการอัปโหลดสกรีนช็อตและแปลงเป็น Base64
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        return Swal.fire('คำเตือน', 'กรุณาเลือกไฟล์รูปภาพขนาดไม่เกิน 2MB', 'warning');
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshot(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim()) return Swal.fire('แจ้งเตือน', 'กรุณากรอกหัวข้อปัญหา', 'warning');
    if (!description.trim()) return Swal.fire('แจ้งเตือน', 'กรุณากรอกรายละเอียดปัญหา', 'warning');

    setIsSubmitting(true);

    const ticketData = {
      id: 'TCK-' + Date.now(),
      subject,
      category,
      priority,
      description,
      screenshot,
      reporterName,
      reporterEmail,
      status: 'pending', // pending, in_progress, resolved, cancelled
      itNotes: '',
      createdAt: new Date().toISOString()
    };

    try {
      // 🚀 พยายามส่งข้อมูลไปยังโฮสต์เซิร์ฟเวอร์ของเรา (api.php)
      const res = await fetch(`${API_URL}?action=create_ticket`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketData)
      });
      const data = await res.json();
      
      if (data.success) {
        Swal.fire('สำเร็จ', 'ส่งตั๋วแจ้งปัญหาไอทีเรียบร้อยแล้ว!', 'success');
      } else {
        throw new Error('API not supported or failed');
      }
    } catch (err) {
      // 🚀 กรณี API หลังบ้านยังไม่มีหรือเออเรอร์ ให้บันทึกสำรองใน LocalStorage เพื่อให้ใช้งานได้ทันที
      console.log('API ticket creation failed or not implemented yet. Saving to LocalStorage instead.');
      const localTickets = localStorage.getItem('helpdesk_tickets');
      const ticketList = localTickets ? JSON.parse(localTickets) : [];
      localStorage.setItem('helpdesk_tickets', JSON.stringify([ticketData, ...ticketList]));
    } finally {
      setIsSubmitting(false);
      Swal.fire({
        title: 'ส่งคำขอสำเร็จ!',
        text: 'เจ้าหน้าที่ไอทีได้รับข้อมูลแล้ว คุณสามารถติดตามสถานะงานได้ในหน้าประวัติตั๋ว',
        icon: 'success',
        confirmButtonText: 'ดูประวัติตั๋วทั้งหมด'
      }).then(() => {
        navigate('/admin/helpdesk/tickets');
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-10 animate-fade-in text-gray-800 dark:text-gray-100">
      
      {/* ส่วนหัวหน้าจอ */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-2xl">
          <FiLifeBuoy size={28} className="animate-spin-slow" />
        </div>
        <div>
          <h1 className="text-2xl font-black">🛟 แจ้งปัญหาไอที / ขอเพิ่มระบบ</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            พบปัญหาระบบขัดข้อง อยากแจ้งบั๊ก หรือมีไอเดียฟีเจอร์ใหม่ ๆ ส่งตรงให้ทีมพัฒนาดูแลได้ทันที
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* คอนโทรลด้านซ้าย - คำแนะนำและข้อมูลผู้แจ้ง */}
        <div className="md:col-span-1 space-y-4">
          <div className="bg-white dark:bg-[#1e293b] p-5 rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm space-y-4">
            <h3 className="font-bold text-sm text-gray-400 uppercase tracking-wider">👤 ข้อมูลผู้แจ้งเรื่อง</h3>
            <div className="space-y-3 text-xs">
              <div>
                <p className="text-gray-400">ผู้แจ้ง</p>
                <p className="font-bold mt-0.5">{reporterName}</p>
              </div>
              <div>
                <p className="text-gray-400">อีเมล/บัญชีผู้ใช้งาน</p>
                <p className="font-bold mt-0.5 truncate">{reporterEmail}</p>
              </div>
              <div className="pt-2 border-t border-gray-200 dark:border-[#334155]">
                <p className="text-gray-400">สิทธิ์การทำงาน</p>
                <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 font-bold mt-1">
                  ADMIN / CRM
                </span>
              </div>
            </div>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-950/20 p-5 rounded-2xl border border-indigo-100 dark:border-indigo-900/40 text-xs leading-relaxed space-y-2">
            <h4 className="font-black text-indigo-800 dark:text-indigo-400 flex items-center gap-1">
              <FiAlertCircle /> คำแนะนำการส่งข้อมูล
            </h4>
            <p className="text-indigo-700 dark:text-indigo-300">
              เพื่อให้เจ้าหน้าที่ไอทีสามารถตรวจสอบและแก้ไขปัญหาให้ได้รวดเร็วที่สุด:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300">
              <li>กรอกชื่อเมนูหรือฟังก์ชันที่เกิดปัญหา</li>
              <li>อธิบายขั้นตอนที่ทำให้เกิดเออเรอร์</li>
              <li>แนบรูปภาพหน้าจอของจุดที่ผิดพลาด</li>
            </ul>
          </div>
        </div>

        {/* คอนโทรลด้านขวา - ฟอร์มกรอกข้อมูล */}
        <form onSubmit={handleSubmit} className="md:col-span-2 bg-white dark:bg-[#1e293b] p-6 rounded-2xl border border-gray-200 dark:border-[#334155] shadow-sm space-y-5">
          
          {/* หัวข้อเรื่อง */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-400">หัวข้อปัญหา / ระบบที่ต้องการให้เพิ่ม <span className="text-red-500">*</span></label>
            <input 
              type="text" 
              placeholder="ระบุชื่อปัญหาหลัก เช่น ปุ่ม PDF กดแล้วไม่ตอบสนอง, อยากให้เพิ่มช่องค้นหาพนักงาน"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-sm dark:text-white outline-none focus:border-blue-500 transition-colors"
              required
            />
          </div>

          {/* หมวดหมู่ & ลำดับความสำคัญ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-400">หมวดหมู่ของเรื่อง</label>
              <select 
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-sm font-bold text-gray-700 dark:text-gray-300 outline-none focus:border-blue-500 transition-colors"
              >
                <option value="bug">🐛 รายงานบั๊ก / ระบบผิดพลาด (Bug)</option>
                <option value="feature">✨ ขอเพิ่มฟีเจอร์ / ฟังก์ชันระบบ (Feature)</option>
                <option value="hardware">🔌 ปัญหาอุปกรณ์ / เครือข่าย (Hardware)</option>
                <option value="access">🔑 สิทธิ์การเข้าถึง / บัญชี (Access)</option>
                <option value="general">❓ สอบถามการใช้งานทั่วไป (General)</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-400">ระดับความเร่งด่วน</label>
              <select 
                value={priority}
                onChange={e => setPriority(e.target.value)}
                className="w-full p-2.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-sm font-bold text-gray-700 dark:text-gray-300 outline-none focus:border-blue-500 transition-colors"
              >
                <option value="low">🟢 ต่ำ (Low) - สอบถามข้อมูลทั่วไป</option>
                <option value="medium">🟡 ปานกลาง (Medium) - ฟังก์ชันรองขัดข้อง</option>
                <option value="high">🟠 สูง (High) - ส่งผลต่อการทำงานหลัก</option>
                <option value="critical">🔴 วิกฤต (Critical) - ระบบล่ม/ทำรายการต่อไม่ได้</option>
              </select>
            </div>
          </div>

          {/* รายละเอียดปัญหา */}
          <div className="space-y-1">
            <label className="text-xs font-bold text-gray-400">รายละเอียดปัญหา / ขั้นตอนที่เกิดเหตุ <span className="text-red-500">*</span></label>
            <textarea 
              placeholder="กรุณาอธิบายรายละเอียดการเกิดเหตุ เช่น เมื่อคลิกปุ่มพิมพ์ใบเสร็จ ตัวเลขอินพุตไม่เปลี่ยนแปลง หรือ อธิบายรูปแบบระบบที่อยากให้เพิ่มเพิ่มเติม..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows="5"
              className="w-full p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-300 dark:border-[#334155] rounded-xl text-sm dark:text-white outline-none focus:border-blue-500 transition-colors resize-none"
              required
            />
          </div>

          {/* อัปโหลดรูปภาพ */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-400">แนบสกรีนช็อตภาพหน้าจอที่พบปัญหา</label>
            
            {!screenshot ? (
              <div className="border-2 border-dashed border-gray-300 dark:border-[#334155] rounded-xl p-6 flex flex-col items-center justify-center gap-2 hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors cursor-pointer relative bg-gray-50/50 dark:bg-[#0f172a]/20">
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                />
                <FiImage size={32} className="text-gray-400 animate-pulse" />
                <p className="text-xs font-bold text-gray-600 dark:text-gray-300">คลิกเพื่อเลือกไฟล์ หรือ ลากรูปภาพมาวางที่นี่</p>
                <p className="text-[10px] text-gray-400">รองรับไฟล์รูปภาพ PNG, JPG ขนาดไม่เกิน 2MB</p>
              </div>
            ) : (
              <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-[#334155] bg-gray-100 max-w-sm">
                <img 
                  src={screenshot} 
                  alt="Screenshot preview" 
                  className="w-full h-auto object-cover max-h-48"
                />
                <button 
                  type="button"
                  onClick={handleRemoveScreenshot}
                  className="absolute top-2 right-2 p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full transition shadow-md"
                  title="ลบรูปภาพ"
                >
                  <FiX size={14} />
                </button>
              </div>
            )}
          </div>

          {/* ปุ่มส่งฟอร์ม */}
          <button 
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl flex justify-center items-center gap-2 text-sm shadow-md transition ${isSubmitting ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <FiSend size={16} /> {isSubmitting ? 'กำลังส่งข้อมูล...' : 'ส่งตั๋วแจ้งปัญหาให้ทีมพัฒนา'}
          </button>

        </form>

      </div>

    </div>
  );
}
